import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  LabelPrintingMethod,
  LABEL_PRINTING_METHOD_TO_DISPLAY_STRING,
  LEGACY_PRINTING_METHODS,
} from 'label-printing/LabelPrintingConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import BaseEditableField from 'ui-library/BaseEditableField';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import useDraftState from 'ui-library/hooks/useDraftState';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

export function LegacyPrintMethodWarning({
  persistedPrintMethod,
}: {
  persistedPrintMethod: LabelPrintingMethod;
}): JSX.Element {
  return (
    <div className={css(styles.marginLeftMinus4)}>
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        text={
          <>
            If you want to switch back to the{' '}
            <em>{LABEL_PRINTING_METHOD_TO_DISPLAY_STRING[persistedPrintMethod].toLowerCase()}</em> option,
            you'll need to contact Benchling Support.
          </>
        }
      />
    </div>
  );
}

interface PrintMethodInputProps {
  printMethods: Array<LabelPrintingMethod>;
  selectedPrintMethod: LabelPrintingMethod;
  onSelectPrintMethod: (newMethod: LabelPrintingMethod) => void;
}

export function PrintMethodInput({
  printMethods,
  selectedPrintMethod,
  onSelectPrintMethod,
}: PrintMethodInputProps): JSX.Element {
  return (
    <div className={css(styles.dropdownFullWidth)}>
      <SelectDropdown
        displayName={LABEL_PRINTING_METHOD_TO_DISPLAY_STRING[selectedPrintMethod]}
        menu={
          <Menu value={selectedPrintMethod} isMulti={false}>
            {printMethods.map((method) => (
              <Option
                value={method}
                key={method}
                onSelect={onSelectPrintMethod}
                dataTestAttrs={{component: 'PrintMethodOption', key: method}}
              >
                {LABEL_PRINTING_METHOD_TO_DISPLAY_STRING[method]}
              </Option>
            ))}
          </Menu>
        }
        dataTestAttrs={{component: 'PrintMethodInput', element: 'dropdown'}}
      />
    </div>
  );
}

export function PrintMethodDisplay({
  persistedPrintMethod,
}: {
  persistedPrintMethod: LabelPrintingMethod;
}): JSX.Element {
  return (
    <span className={css(styles.viewer)}>
      {LABEL_PRINTING_METHOD_TO_DISPLAY_STRING[persistedPrintMethod]}
    </span>
  );
}

export interface EditablePrintMethodProps {
  isWritable: boolean;
  onChange: (newPrintingMethod: LabelPrintingMethod) => void;
  persistedPrintMethod: LabelPrintingMethod;
}

export default function EditablePrintMethod(props: EditablePrintMethodProps): JSX.Element {
  const {
    data: draftData,
    updateDraftData,
    isEditing,
    startEditing,
    stopEditing,
  } = useDraftState<{
    selectedPrintMethod: LabelPrintingMethod;
  }>({selectedPrintMethod: props.persistedPrintMethod});

  const printMethodsToOffer: Array<LabelPrintingMethod> = [
    ...(LEGACY_PRINTING_METHODS.includes(props.persistedPrintMethod) ? [props.persistedPrintMethod] : []),
    LabelPrintingMethod.BARTENDER,
    LabelPrintingMethod.ZEBRA_BROWSER_PRINT,
  ];

  return (
    <>
      <BaseEditableField
        editor={
          <PrintMethodInput
            printMethods={printMethodsToOffer}
            selectedPrintMethod={draftData.selectedPrintMethod}
            onSelectPrintMethod={(selectedPrintMethod) => {
              updateDraftData({selectedPrintMethod});
            }}
          />
        }
        viewer={<PrintMethodDisplay persistedPrintMethod={props.persistedPrintMethod} />}
        isBeingEdited={isEditing}
        isWritable={props.isWritable}
        isCompleteEnabled={true}
        onEdit={startEditing}
        onCancel={stopEditing}
        onComplete={() => {
          props.onChange(draftData.selectedPrintMethod);
          stopEditing();
        }}
      />

      {LEGACY_PRINTING_METHODS.includes(props.persistedPrintMethod) &&
        draftData.selectedPrintMethod !== props.persistedPrintMethod && (
          <LegacyPrintMethodWarning persistedPrintMethod={props.persistedPrintMethod} />
        )}
    </>
  );
}

const styles = StyleSheet.create({
  viewer: {
    maxWidth: '100%',
    overflowWrap: 'normal',
  },
  dropdownFullWidth: {
    width: '100%',
  },
  marginLeftMinus4: {
    marginLeft: '-4px',
  },
});
